export type ApiErrorBody = {
  error?: {
    code?: string
    message?: string
    field_errors?: Record<string, string>
    request_id?: string
  }
  detail?: string
}

export const AUTH_EXPIRED_EVENT = 'partnership:auth-expired'

export type UploadResult = {
  items: Array<{ id: string; original_name: string }>
  errors: Array<{ filename: string; message: string }>
}

export class ApiError extends Error {
  status: number
  fields: Record<string, string>
  requestId?: string

  constructor(status: number, body: ApiErrorBody) {
    super(body.error?.message || body.detail || 'The request could not be completed.')
    this.status = status
    this.fields = body.error?.field_errors || {}
    this.requestId = body.error?.request_id
  }
}

function cookie(name: string) {
  return document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`))
    ?.slice(name.length + 1)
}

export async function api<T>(
  path: string,
  options: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const headers = new Headers(options.headers)
  if (options.json !== undefined) headers.set('Content-Type', 'application/json')
  const method = (options.method || 'GET').toUpperCase()
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = cookie('partnership_csrf')
    if (csrf) headers.set('X-CSRF-Token', decodeURIComponent(csrf))
  }
  const response = await fetch(`/api${path}`, {
    ...options,
    headers,
    credentials: 'include',
    body: options.json === undefined ? options.body : JSON.stringify(options.json),
  })
  if (!response.ok) {
    if (response.status === 401 && !path.startsWith('/auth/')) {
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT))
    }
    let body: ApiErrorBody = {}
    try {
      body = await response.json()
    } catch {
      body = { detail: `Request failed (${response.status})` }
    }
    throw new ApiError(response.status, body)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export async function download(path: string, options: RequestInit = {}) {
  const csrf = cookie('partnership_csrf')
  const headers = new Headers(options.headers)
  if (csrf) headers.set('X-CSRF-Token', decodeURIComponent(csrf))
  const response = await fetch(`/api${path}`, { ...options, headers, credentials: 'include' })
  if (!response.ok) throw new ApiError(response.status, await response.json())
  const blob = await response.blob()
  const disposition = response.headers.get('content-disposition') || ''
  const name = disposition.match(/filename="([^"]+)"/)?.[1] || 'download'
  const anchor = document.createElement('a')
  anchor.href = URL.createObjectURL(blob)
  anchor.download = name
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(anchor.href), 60_000)
}

type SaveFileHandle = {
  createWritable: () => Promise<{
    write: (data: Blob) => Promise<void>
    close: () => Promise<void>
  }>
}

type SaveFilePickerWindow = Window & {
  showSaveFilePicker?: (options: { suggestedName: string }) => Promise<SaveFileHandle>
}

export async function saveDownload(path: string, suggestedName: string) {
  const picker = navigator.webdriver
    ? undefined
    : (window as SaveFilePickerWindow).showSaveFilePicker
  if (!picker) {
    const anchor = document.createElement('a')
    anchor.href = `/api${path}`
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    return
  }

  let handle: SaveFileHandle
  try {
    handle = await picker.call(window, { suggestedName })
  } catch (reason) {
    if (reason instanceof DOMException && reason.name === 'AbortError') return
    throw reason
  }

  const response = await fetch(`/api${path}`, { credentials: 'include' })
  if (!response.ok) throw new ApiError(response.status, await response.json())
  const writable = await handle.createWritable()
  await writable.write(await response.blob())
  await writable.close()
}

export async function uploadAttachments(
  files: File[],
  resourceType: string,
  resourceId: string,
) {
  if (!files.length) return { items: [], errors: [] }
  const body = new FormData()
  files.forEach((file) => body.append('files', file))
  body.set('resource_type', resourceType)
  body.set('resource_id', resourceId)
  body.set('kind', 'Other business file')
  body.set('safety', 'Unreviewed')
  return api<UploadResult>('/files', {
    method: 'POST',
    body,
  })
}
